import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, LogIn, Loader2, Users, Package, CreditCard, Star,
  RefreshCw, DollarSign, Database, Gift, Ticket, TrendingUp,
  ChevronRight, ArrowLeft, Settings, Wallet, ShoppingBag,
  Search, ChevronLeft, Eye, MessageSquare, Download, Trash2,
  RotateCcw, AlertTriangle, CheckCircle, Clock, Wifi, BarChart3,
  HardDrive, Cloud, CloudUpload, Play, Calendar, FileText,
  Building2, Globe, ChevronDown, Archive, Mail, Import, Smartphone
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ──────────── LOGIN ──────────── */
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('admin_token', res.data.token);
      onLogin(res.data.token);
    } catch {
      setError('Credenziali non valide');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-slate-500 text-sm">eSIM4You Management</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-slate-400 text-xs uppercase tracking-wider mb-1 block">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-all"
                placeholder="admin@..." required data-testid="admin-email" />
            </div>
            <div>
              <label className="text-slate-400 text-xs uppercase tracking-wider mb-1 block">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-600 focus:border-amber-500/50 focus:outline-none transition-all"
                placeholder="********" required data-testid="admin-password" />
            </div>
            {error && <p className="text-rose-400 text-sm">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="admin-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Accedi
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

/* ──────────── STAT CARD ──────────── */
function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-white font-mono">{value}</p>
      <p className="text-slate-500 text-xs mt-1">{label}</p>
      {sub && <p className="text-slate-600 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

/* ──────────── MARKUP CONTROL ──────────── */
function MarkupControl({ currentMarkup, token, onUpdate }) {
  const [markup, setMarkup] = useState(currentMarkup);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { setMarkup(currentMarkup); }, [currentMarkup]);

  const presets = [
    { label: '1.5x', value: 1.5, desc: 'Competitivo' },
    { label: '2.0x', value: 2.0, desc: 'Standard' },
    { label: '2.5x', value: 2.5, desc: 'Premium' },
    { label: '3.0x', value: 3.0, desc: 'Alto margine' },
  ];

  const applyMarkup = async () => {
    setApplying(true);
    setResult(null);
    try {
      await axios.post(`${API}/admin/set-markup`, { markup }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult({ success: true, msg: `Markup ${markup}x applicato! Catalogo in aggiornamento (~2 min).` });
      onUpdate();
    } catch (e) {
      setResult({ success: false, msg: e.response?.data?.detail || 'Errore' });
    }
    setApplying(false);
  };

  const exampleCost = 0.83;
  const exampleRetail = (exampleCost * 0.92 * markup).toFixed(2);
  const exampleProfit = (exampleRetail - exampleCost * 0.92).toFixed(2);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="markup-control">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-amber-500/20 rounded-lg flex items-center justify-center">
          <DollarSign className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Markup Prezzi</h3>
          <p className="text-slate-500 text-xs">Costo fornitore x markup = prezzo cliente</p>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {presets.map(p => (
          <button key={p.value} onClick={() => setMarkup(p.value)}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${markup === p.value ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'}`}
            data-testid={`markup-preset-${p.value}`}>
            <div className="font-bold">{p.label}</div>
            <div className="text-[10px] opacity-70">{p.desc}</div>
          </button>
        ))}
      </div>
      <div className="mb-4">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>1.0x</span><span className="text-amber-400 font-bold text-sm">{markup}x</span><span>5.0x</span>
        </div>
        <input type="range" min="1.0" max="5.0" step="0.1" value={markup}
          onChange={e => setMarkup(parseFloat(e.target.value))} className="w-full accent-amber-500" data-testid="markup-slider" />
      </div>
      <div className="bg-black/30 rounded-xl p-4 mb-4">
        <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Anteprima - Italia 1GB/3gg</p>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><p className="text-slate-500 text-[10px]">Costo</p><p className="text-white font-mono font-bold">$0.83</p></div>
          <div><p className="text-slate-500 text-[10px]">Prezzo cliente</p><p className="text-amber-400 font-mono font-bold">{"\u20AC"}{exampleRetail}</p></div>
          <div><p className="text-slate-500 text-[10px]">Profitto</p><p className="text-emerald-400 font-mono font-bold">{"\u20AC"}{exampleProfit}</p></div>
        </div>
      </div>
      <button onClick={applyMarkup} disabled={applying || markup === currentMarkup}
        className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
        data-testid="apply-markup">
        {applying ? <><Loader2 className="w-4 h-4 animate-spin" />Aggiornamento catalogo...</> : <><Settings className="w-4 h-4" />Applica Markup {markup}x</>}
      </button>
      {result && <p className={`mt-3 text-sm text-center ${result.success ? 'text-emerald-400' : 'text-rose-400'}`}>{result.msg}</p>}
    </div>
  );
}

/* ──────────── ZONE MARKUP CONTROL ──────────── */
const ZONE_LABELS = {
  'europe': 'Europa', 'asia': 'Asia', 'north-america': 'Nord America',
  'south-america': 'Sud America', 'middle-east': 'Medio Oriente',
  'africa': 'Africa', 'caribbean': 'Caraibi', 'oceania': 'Oceania'
};

function ZoneMarkupControl({ pricing, token, onUpdate }) {
  const globalMarkup = pricing?.markup || 2.0;
  const [zones, setZones] = useState({});
  const [countries, setCountries] = useState({});
  const [newCC, setNewCC] = useState('');
  const [newCCMarkup, setNewCCMarkup] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingCountry, setSavingCountry] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setZones(pricing?.zone_markups || {});
    setCountries(pricing?.country_markups || {});
  }, [pricing]);

  const handleZoneChange = (slug, val) => {
    const num = parseFloat(val);
    if (!isNaN(num)) setZones(prev => ({ ...prev, [slug]: num }));
  };

  const saveZones = async () => {
    setSaving(true); setResult(null);
    try {
      await axios.post(`${API}/admin/zone-markups`, { zones }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult({ ok: true, msg: 'Markup per zona salvati! Catalogo in aggiornamento.' });
      onUpdate();
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || 'Errore' });
    }
    setSaving(false);
  };

  const addCountry = async () => {
    const cc = newCC.toLowerCase().trim();
    const val = parseFloat(newCCMarkup);
    if (!cc || cc.length !== 2 || isNaN(val) || val < 1 || val > 10) return;
    setSavingCountry(true);
    try {
      const res = await axios.post(`${API}/admin/country-markup`, { country_code: cc, markup: val }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(res.data.countries);
      setNewCC(''); setNewCCMarkup('');
      onUpdate();
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || 'Errore' });
    }
    setSavingCountry(false);
  };

  const removeCountry = async (cc) => {
    try {
      const res = await axios.post(`${API}/admin/country-markup`, { country_code: cc, delete: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(res.data.countries);
      onUpdate();
    } catch {}
  };

  const exampleCost = 0.83;
  const usdEur = pricing?.usd_to_eur || 0.92;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:col-span-2" data-testid="zone-markup-control">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Markup per Zona e Paese</h3>
          <p className="text-slate-500 text-xs">Priorit&agrave;: Paese &rarr; Zona &rarr; Globale ({globalMarkup}x)</p>
        </div>
      </div>

      {/* Zone markups grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {Object.entries(ZONE_LABELS).map(([slug, label]) => {
          const val = zones[slug];
          const active = val !== undefined && val !== null;
          const effectiveMarkup = active ? val : globalMarkup;
          const preview = (exampleCost * usdEur * effectiveMarkup).toFixed(2);
          return (
            <div key={slug} className={`rounded-xl p-3 border transition-all ${active ? 'bg-blue-500/10 border-blue-500/30' : 'bg-white/3 border-white/5'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300 text-xs font-medium">{label}</span>
                {active && <button onClick={() => setZones(prev => { const n = {...prev}; delete n[slug]; return n; })}
                  className="text-slate-600 hover:text-rose-400 transition-colors"><Trash2 className="w-3 h-3" /></button>}
              </div>
              <input type="number" min="1.0" max="10.0" step="0.1"
                value={active ? val : ''}
                placeholder={`${globalMarkup}`}
                onChange={e => handleZoneChange(slug, e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-blue-500/50 focus:outline-none placeholder-slate-600"
                data-testid={`zone-markup-${slug}`} />
              <p className="text-slate-500 text-[10px] mt-1">Anteprima: <span className={active ? 'text-blue-400' : 'text-slate-500'}>&euro;{preview}</span></p>
            </div>
          );
        })}
      </div>

      <button onClick={saveZones} disabled={saving}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 mb-5 text-sm"
        data-testid="save-zone-markups">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
        Salva Markup Zone
      </button>

      {result && <p className={`mb-4 text-sm text-center ${result.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{result.msg}</p>}

      {/* Country overrides */}
      <div className="border-t border-white/10 pt-5">
        <p className="text-slate-400 text-xs uppercase tracking-wider mb-3 font-semibold">Override per Paese</p>

        {Object.keys(countries).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(countries).sort((a,b) => a[0].localeCompare(b[0])).map(([cc, val]) => (
              <div key={cc} className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5">
                <img src={`https://flagcdn.com/w20/${cc}.png`} alt={cc} className="w-4 h-3 rounded-sm" />
                <span className="text-white text-xs font-mono font-bold">{cc.toUpperCase()}</span>
                <span className="text-amber-400 text-xs font-mono">{val}x</span>
                <button onClick={() => removeCountry(cc)} className="text-slate-500 hover:text-rose-400 ml-1 transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-slate-500 text-[10px] uppercase mb-1 block">Codice Paese (2 lettere)</label>
            <input type="text" maxLength={2} value={newCC} onChange={e => setNewCC(e.target.value.toUpperCase())}
              placeholder="IT" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-amber-500/50 focus:outline-none placeholder-slate-600 uppercase"
              data-testid="country-override-code" />
          </div>
          <div className="flex-1">
            <label className="text-slate-500 text-[10px] uppercase mb-1 block">Markup</label>
            <input type="number" min="1.0" max="10.0" step="0.1" value={newCCMarkup} onChange={e => setNewCCMarkup(e.target.value)}
              placeholder="1.6" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-amber-500/50 focus:outline-none placeholder-slate-600"
              data-testid="country-override-markup" />
          </div>
          <button onClick={addCountry} disabled={savingCountry || !newCC || !newCCMarkup}
            className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-semibold px-4 py-2 rounded-lg transition-all text-sm whitespace-nowrap"
            data-testid="add-country-override">
            {savingCountry ? <Loader2 className="w-4 h-4 animate-spin" /> : '+ Aggiungi'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────── NETWORK ALERTS TAB ──────────── */
function NetworkAlertsTab({ token }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', country_codes: '', severity: 'warning', start_date: '', end_date: '' });

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/network-alerts`, { headers: { Authorization: `Bearer ${token}` } });
      setAlerts(res.data.alerts || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const createAlert = async () => {
    setSaving(true);
    try {
      const codes = form.country_codes.split(',').map(c => c.trim().toLowerCase()).filter(c => c.length === 2);
      await axios.post(`${API}/admin/network-alerts`, {
        ...form, country_codes: codes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ title: '', description: '', country_codes: '', severity: 'warning', start_date: '', end_date: '' });
      setShowForm(false);
      fetchAlerts();
    } catch {}
    setSaving(false);
  };

  const resolveAlert = async (id) => {
    try {
      await axios.post(`${API}/admin/network-alerts/${id}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchAlerts();
    } catch {}
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/admin/network-alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAlerts();
    } catch {}
  };

  const sevColors = {
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    critical: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="network-alerts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-lg">Avvisi Rete</h3>
          <p className="text-slate-500 text-xs">{alerts.filter(a => a.active).length} attivi &middot; {alerts.length} totali</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2 rounded-lg text-sm transition-all"
          data-testid="new-alert-btn">
          {showForm ? 'Annulla' : '+ Nuovo Avviso'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="alert-form">
          <div>
            <label className="text-slate-400 text-xs uppercase mb-1 block">Titolo *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="es. Interruzione rete mobile a Bali"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none placeholder-slate-600"
              data-testid="alert-title" />
          </div>
          <div>
            <label className="text-slate-400 text-xs uppercase mb-1 block">Descrizione</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Dettagli sul problema, data prevista di risoluzione..."
              rows={3} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none placeholder-slate-600 resize-none"
              data-testid="alert-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-xs uppercase mb-1 block">Paesi interessati</label>
              <input value={form.country_codes} onChange={e => setForm(f => ({ ...f, country_codes: e.target.value }))}
                placeholder="ID, TH, MY (vuoto = globale)"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none placeholder-slate-600 uppercase"
                data-testid="alert-countries" />
              <p className="text-slate-600 text-[10px] mt-1">Codici a 2 lettere separati da virgola, vuoto per avviso globale</p>
            </div>
            <div>
              <label className="text-slate-400 text-xs uppercase mb-1 block">Gravit&agrave;</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'info', label: 'Info', color: 'blue' },
                  { value: 'warning', label: 'Warning', color: 'amber' },
                  { value: 'critical', label: 'Critico', color: 'rose' },
                ].map(s => (
                  <button key={s.value} onClick={() => setForm(f => ({ ...f, severity: s.value }))}
                    className={`py-2 rounded-lg text-xs font-medium transition-all ${form.severity === s.value ? `bg-${s.color}-500/20 text-${s.color}-400 border border-${s.color}-500/40` : 'bg-white/5 text-slate-500 border border-white/10'}`}
                    data-testid={`severity-${s.value}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-xs uppercase mb-1 block">Data inizio</label>
              <input type="text" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                placeholder="19 Marzo 2026, 06:00"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none placeholder-slate-600"
                data-testid="alert-start" />
            </div>
            <div>
              <label className="text-slate-400 text-xs uppercase mb-1 block">Data fine</label>
              <input type="text" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                placeholder="20 Marzo 2026, 06:00"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none placeholder-slate-600"
                data-testid="alert-end" />
            </div>
          </div>
          <button onClick={createAlert} disabled={saving || !form.title}
            className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
            data-testid="create-alert-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pubblica Avviso'}
          </button>
        </div>
      )}

      {/* Alerts list */}
      <div className="space-y-3">
        {alerts.length === 0 && <p className="text-slate-500 text-sm text-center py-8">Nessun avviso di rete</p>}
        {alerts.map(alert => (
          <div key={alert.id} className={`border rounded-xl p-4 ${alert.active ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-60'}`}
            data-testid={`alert-card-${alert.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${sevColors[alert.severity] || sevColors.warning}`}>
                    {alert.severity}
                  </span>
                  {alert.active ? (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Attivo</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/5 text-slate-500 border border-white/10">Risolto</span>
                  )}
                  {alert.country_codes?.length > 0 && alert.country_codes.map(cc => (
                    <span key={cc} className="inline-flex items-center gap-1">
                      <img src={`https://flagcdn.com/w20/${cc}.png`} alt={cc} className="w-4 h-3 rounded-sm" />
                      <span className="text-slate-400 text-[10px] font-mono">{cc.toUpperCase()}</span>
                    </span>
                  ))}
                </div>
                <p className="text-white font-semibold text-sm">{alert.title}</p>
                {alert.description && <p className="text-slate-400 text-xs mt-1">{alert.description}</p>}
                <p className="text-slate-600 text-[10px] mt-2">
                  {alert.start_date && <span>{alert.start_date}{alert.end_date ? ` — ${alert.end_date}` : ''} &middot; </span>}
                  Creato: {new Date(alert.created_at).toLocaleString('it-IT')}
                  {alert.resolved_at && <span> &middot; Risolto: {new Date(alert.resolved_at).toLocaleString('it-IT')}</span>}
                </p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                {alert.active && (
                  <button onClick={() => resolveAlert(alert.id)}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    data-testid={`resolve-alert-${alert.id}`}>
                    Risolvi
                  </button>
                )}
                <button onClick={() => deleteAlert(alert.id)}
                  className="bg-white/5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-lg transition-all"
                  data-testid={`delete-alert-${alert.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────── ORDERS TAB ──────────── */
function OrdersTab({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [note, setNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);
  const [resendingQr, setResendingQr] = useState(false);
  const [resendQrResult, setResendQrResult] = useState(null);
  const [resendingReview, setResendingReview] = useState(false);
  const [resendReviewResult, setResendReviewResult] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState('');
  const [overrideEmail2, setOverrideEmail2] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        params: { status: filter, page, per_page: 15, search },
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data.orders);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, filter, page, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const addNote = async () => {
    if (!note.trim() || !selectedOrder) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/admin/orders/${selectedOrder.session_id}/note`,
        { note: note.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNote('');
      const res = await axios.get(`${API}/admin/orders/${selectedOrder.session_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(res.data.order);
    } catch { /* ignore */ }
    setAddingNote(false);
  };

  const retryEsim = async () => {
    if (!selectedOrder) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      const res = await axios.post(`${API}/admin/orders/${selectedOrder.session_id}/retry-esim`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRetryResult({ success: true, msg: res.data.message });
      // Refresh order
      const fresh = await axios.get(`${API}/admin/orders/${selectedOrder.session_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(fresh.data.order);
    } catch (e) {
      setRetryResult({ success: false, msg: e.response?.data?.detail || 'Errore nel retry' });
    }
    setRetrying(false);
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/orders/export`, {
        params: { status: filter },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordini_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { /* ignore */ }
    setExporting(false);
  };

  const statusColors = {
    paid: 'bg-emerald-500/20 text-emerald-400',
    completed: 'bg-emerald-500/20 text-emerald-400',
    initiated: 'bg-amber-500/20 text-amber-400',
    pending: 'bg-amber-500/20 text-amber-400',
    failed: 'bg-rose-500/20 text-rose-400',
    expired: 'bg-slate-500/20 text-slate-400',
  };

  /* ── Order Detail View ── */
  if (selectedOrder) {
    const o = selectedOrder;
    const canRetry = o.payment_status === 'paid' && !o.esim_provisioned;

    return (
      <div className="space-y-4" data-testid="order-detail">
        <button onClick={() => { setSelectedOrder(null); setRetryResult(null); }}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-all">
          <ArrowLeft className="w-4 h-4" /> Torna agli ordini
        </button>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-lg">Ordine #{o.session_id?.slice(-8)}</h3>
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[o.payment_status] || 'bg-slate-500/20 text-slate-400'}`}>
              {o.payment_status?.toUpperCase()}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div><p className="text-slate-500 text-xs">Importo</p><p className="text-white font-mono font-bold">{"\u20AC"}{o.amount}</p></div>
            <div><p className="text-slate-500 text-xs">Metodo</p><p className="text-white capitalize">{o.payment_method || 'stripe'}</p></div>
            <div><p className="text-slate-500 text-xs">Data</p><p className="text-white text-sm">{new Date(o.created_at).toLocaleString('it-IT')}</p></div>
            <div><p className="text-slate-500 text-xs">Session ID</p><p className="text-white font-mono text-xs break-all">{o.session_id}</p></div>
          </div>

          {o.discount_amount > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4 text-sm">
              <span className="text-amber-400">Sconto applicato: </span>
              <span className="text-white font-mono">{"\u20AC"}{o.discount_amount}</span>
              <span className="text-slate-400"> (coupon: {o.coupon_code})</span>
            </div>
          )}

          {/* Customer Info */}
          <div className="border-t border-white/10 pt-4 mb-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wider mb-2">Cliente</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Nome: </span><span className="text-white">{o.billing?.first_name} {o.billing?.last_name}</span></div>
              <div><span className="text-slate-500">Email: </span><span className="text-white">{o.billing?.email}</span></div>
              {o.billing?.phone && <div><span className="text-slate-500">Tel: </span><span className="text-white">{o.billing?.phone}</span></div>}
              {o.billing?.country && <div><span className="text-slate-500">Paese: </span><span className="text-white">{o.billing?.country}</span></div>}
            </div>
          </div>

          {/* Items */}
          <div className="border-t border-white/10 pt-4 mb-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wider mb-2">Articoli</h4>
            <div className="space-y-2">
              {(o.items || []).map((item, i) => (
                <div key={i} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                  <div>
                    <p className="text-white text-sm font-medium">{item.name}</p>
                    <p className="text-slate-500 text-xs">Qty: {item.quantity || 1} {item.package_type_id ? `| PKG: ${item.package_type_id.slice(0,8)}...` : ''}</p>
                  </div>
                  <p className="text-white font-mono">{"\u20AC"}{item.price}</p>
                </div>
              ))}
            </div>
          </div>

          {/* eSIM Provisioning Status */}
          <div className="border-t border-white/10 pt-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-slate-400 text-xs uppercase tracking-wider">Stato eSIM</h4>
              {o.esim_provisioned ? (
                <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle className="w-3 h-3" /> Provisioned</span>
              ) : o.payment_status === 'paid' ? (
                <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertTriangle className="w-3 h-3" /> Non provisioned</span>
              ) : (
                <span className="flex items-center gap-1 text-slate-500 text-xs"><Clock className="w-3 h-3" /> N/A</span>
              )}
            </div>

            {(o.esim_results || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {o.esim_results.map((esim, i) => (
                  <div key={i} className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-slate-500">ICCID: </span><span className="text-white font-mono text-xs">{esim.iccid || '-'}</span></div>
                      <div><span className="text-slate-500">SIM ID: </span><span className="text-white font-mono text-xs">{esim.sim_id || '-'}</span></div>
                      {esim.qr_code_text && <div className="col-span-2"><span className="text-slate-500">QR: </span><span className="text-teal-300 font-mono text-xs break-all">{esim.qr_code_text}</span></div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {o.esim_details && !o.esim_results?.length && (
              <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3 text-sm mb-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">ICCID: </span><span className="text-white font-mono text-xs">{o.esim_details?.iccid || '-'}</span></div>
                  <div><span className="text-slate-500">Status: </span><span className="text-teal-400">{o.esim_provisioning_status || 'unknown'}</span></div>
                </div>
              </div>
            )}

            {/* Retry Button */}
            {canRetry && (
              <button onClick={retryEsim} disabled={retrying}
                className="w-full bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="retry-esim-btn">
                {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Riprova Provisioning eSIM
              </button>
            )}
            {retryResult && (
              <p className={`mt-2 text-sm text-center ${retryResult.success ? 'text-emerald-400' : 'text-rose-400'}`}>{retryResult.msg}</p>
            )}

            {/* Resend QR Code Email Button */}
            {o.esim_results?.some(e => e.qr_code_text) && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <input
                    value={overrideEmail}
                    onChange={e => setOverrideEmail(e.target.value)}
                    placeholder={o.billing?.email || o.customer_email || "Email 1..."}
                    className="flex-1 bg-white/5 border border-teal-500/20 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-teal-500/50 focus:outline-none"
                    data-testid="override-email-input"
                  />
                  <input
                    value={overrideEmail2}
                    onChange={e => setOverrideEmail2(e.target.value)}
                    placeholder="Email 2 (opzionale)"
                    className="flex-1 bg-white/5 border border-teal-500/20 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-teal-500/50 focus:outline-none"
                    data-testid="override-email-input-2"
                  />
                </div>
                <button onClick={async () => {
                  setResendingQr(true); setResendQrResult(null);
                  try {
                    const emails = [overrideEmail.trim(), overrideEmail2.trim()].filter(Boolean);
                    const body = emails.length > 0 ? { emails } : {};
                    const res = await axios.post(`${API}/admin/resend-qr-email/${o.session_id}`, body, { headers: { Authorization: `Bearer ${token}` } });
                    setResendQrResult({ success: true, msg: res.data.message || 'QR email re-inviata!' });
                  } catch (e) { setResendQrResult({ success: false, msg: e.response?.data?.detail || 'Errore nel re-invio' }); }
                  setResendingQr(false);
                }} disabled={resendingQr}
                  className="w-full bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="resend-qr-btn">
                  {resendingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Re-invia Email QR Code
                </button>
                <p className="text-slate-600 text-xs">Lascia vuoto per inviare a {o.billing?.email || o.customer_email || 'email originale'}. Puoi aggiungere fino a 2 email alternative.</p>
              </div>
            )}
            {resendQrResult && (
              <p className={`mt-2 text-sm text-center ${resendQrResult.success ? 'text-emerald-400' : 'text-rose-400'}`}>{resendQrResult.msg}</p>
            )}

            {/* Resend Review Request */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <h4 className="text-slate-400 text-xs uppercase tracking-wider mb-2">Invito Recensione</h4>
              <button onClick={async () => {
                setResendingReview(true); setResendReviewResult(null);
                try {
                  const res = await axios.post(`${API}/admin/orders/${o.session_id}/resend-review`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  setResendReviewResult({ success: true, msg: res.data.message || 'Invito recensione inviato!' });
                } catch (e) { setResendReviewResult({ success: false, msg: e.response?.data?.detail || 'Errore invio' }); }
                setResendingReview(false);
              }} disabled={resendingReview}
                className="w-full bg-purple-500/20 hover:bg-purple-500/30 disabled:opacity-40 border border-purple-500/30 text-purple-300 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                data-testid="resend-review-btn">
                {resendingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                Reinvia Invito Recensione
              </button>
              {resendReviewResult && (
                <p className={`mt-2 text-sm text-center ${resendReviewResult.success ? 'text-emerald-400' : 'text-rose-400'}`}>{resendReviewResult.msg}</p>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wider mb-2">Note Admin</h4>
            {(o.admin_notes || []).map((n, i) => (
              <div key={i} className="bg-white/5 rounded-lg p-3 mb-2">
                <p className="text-white text-sm">{n.text}</p>
                <p className="text-slate-600 text-xs mt-1">{new Date(n.created_at).toLocaleString('it-IT')}</p>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Aggiungi nota..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none"
                data-testid="order-note-input" />
              <button onClick={addNote} disabled={!note.trim() || addingNote}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1"
                data-testid="add-note-btn">
                {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Orders List View ── */
  return (
    <div className="space-y-4" data-testid="orders-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Cerca per email, nome o ID..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none"
            data-testid="order-search" />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: 'all', label: 'Tutti' },
            { key: 'paid', label: 'Pagati' },
            { key: 'pending', label: 'In attesa' },
            { key: 'failed', label: 'Falliti' },
          ].map(f => (
            <button key={f.key} onClick={() => { setFilter(f.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f.key ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* CSV Export */}
        <button onClick={exportCSV} disabled={exporting}
          className="bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 transition-all disabled:opacity-50"
          data-testid="export-csv-btn">
          {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          CSV
        </button>
      </div>

      <p className="text-slate-500 text-xs">{total} ordini trovati</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Nessun ordine trovato</div>
      ) : (
        <div className="space-y-2">
          {orders.map((o, i) => (
            <motion.div key={o.session_id || i}
              initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => setSelectedOrder(o)}
              className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-amber-500/30 cursor-pointer transition-all group"
              data-testid={`order-row-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center">
                    {o.esim_provisioned ? <Wifi className="w-4 h-4 text-emerald-400" /> : <ShoppingBag className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-medium">
                        {o.billing?.first_name || ''} {o.billing?.last_name || 'Guest'}
                      </p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[o.payment_status] || 'bg-slate-500/20 text-slate-400'}`}>
                        {o.payment_status}
                      </span>
                      {o.payment_status === 'paid' && !o.esim_provisioned && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-rose-500/20 text-rose-400">
                          eSIM pending
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs">
                      {o.billing?.email || 'N/A'} - {(o.items || []).length} {(o.items || []).length === 1 ? 'articolo' : 'articoli'}
                      {o.coupon_code ? ` | ${o.coupon_code}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-white font-mono font-bold">{"\u20AC"}{o.amount || '0.00'}</p>
                    <p className="text-slate-600 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString('it-IT') : ''}</p>
                  </div>
                  <Eye className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition-all" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-slate-400 text-sm px-3">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ──────────── USERS TAB ──────────── */
function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/recent-users`, {
        params: { search, page, per_page: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, search, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <div className="space-y-4" data-testid="users-tab">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Cerca per email o nome..."
          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none"
          data-testid="user-search" />
      </div>

      <p className="text-slate-500 text-xs">{total} utenti</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {users.map((u, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-teal-500/10 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-teal-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{u.name || 'Anonymous'}</p>
                  <p className="text-slate-500 text-xs">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-white font-mono text-sm font-bold">{u.order_count || 0}</p>
                  <p className="text-slate-600 text-[10px]">Ordini</p>
                </div>
                <div className="text-center">
                  <p className="text-emerald-400 font-mono text-sm font-bold">{"\u20AC"}{u.total_spent || 0}</p>
                  <p className="text-slate-600 text-[10px]">Speso</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-xs">{u.referral_code || ''}</p>
                  <p className="text-slate-600 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : ''}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-slate-400 text-sm px-3">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ──────────── COUPONS TAB ──────────── */
function CouponsTab({ token }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newAmount, setNewAmount] = useState(10);
  const [newMaxUses, setNewMaxUses] = useState(0);
  const [newExpiry, setNewExpiry] = useState('');
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/coupons`, {
        params: { status: statusFilter, source: sourceFilter, page, per_page: 20, search },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(res.data.coupons || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, statusFilter, sourceFilter, page, search]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const deleteCoupon = async (code) => {
    if (!window.confirm(`Eliminare il coupon ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCoupons();
    } catch { /* ignore */ }
  };

  const createCoupon = async () => {
    if (!newCode.trim()) return;
    setCreating(true);
    setCreateResult(null);
    try {
      const body = { code: newCode.trim(), amount: newAmount, max_uses: newMaxUses };
      if (newExpiry) body.expires_at = new Date(newExpiry).toISOString();
      const res = await axios.post(`${API}/admin/coupons/create`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCreateResult({ success: true, msg: `Coupon ${res.data.coupon.code} creato!` });
        setNewCode(''); setNewAmount(10); setNewMaxUses(0); setNewExpiry('');
        setShowCreate(false);
        fetchCoupons();
      }
    } catch (e) {
      setCreateResult({ success: false, msg: e.response?.data?.detail || 'Errore creazione coupon' });
    }
    setCreating(false);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    try { return new Date() > new Date(expiresAt); } catch { return false; }
  };

  const sourceLabels = {
    review_reward: 'Recensione',
    welcome: 'Benvenuto',
    referral_reward: 'Referral',
    manual: 'Manuale',
  };

  const sourceColors = {
    review_reward: 'bg-yellow-500/20 text-yellow-400',
    welcome: 'bg-blue-500/20 text-blue-400',
    referral_reward: 'bg-purple-500/20 text-purple-400',
    manual: 'bg-slate-500/20 text-slate-400',
  };

  return (
    <div className="space-y-4" data-testid="coupons-tab">
      {/* Create Result Message */}
      {createResult && (
        <div className={`p-3 rounded-lg text-sm ${createResult.success ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'}`}>
          {createResult.msg}
        </div>
      )}

      {/* Create Coupon Form */}
      {showCreate && (
        <div className="bg-white/5 border border-amber-500/20 rounded-2xl p-5 space-y-4">
          <h4 className="text-white font-semibold text-sm">Crea Coupon Personalizzato</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Codice</label>
              <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())}
                placeholder="es. TRAVEL10"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none font-mono"
                data-testid="new-coupon-code" />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Sconto %</label>
              <input type="number" value={newAmount} onChange={e => setNewAmount(Number(e.target.value))}
                min="1" max="100"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                data-testid="new-coupon-amount" />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Max utilizzi (0=illimitato)</label>
              <input type="number" value={newMaxUses} onChange={e => setNewMaxUses(Number(e.target.value))}
                min="0"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                data-testid="new-coupon-max-uses" />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Scadenza (opzionale)</label>
              <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                data-testid="new-coupon-expiry" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createCoupon} disabled={creating || !newCode.trim()}
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              data-testid="create-coupon-submit">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ticket className="w-3.5 h-3.5" />}
              Crea Coupon
            </button>
            <button onClick={() => { setShowCreate(false); setCreateResult(null); }}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 px-4 py-2 rounded-lg text-sm transition-all">
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Cerca codice coupon..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none"
            data-testid="coupon-search" />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: 'all', label: 'Tutti' },
            { key: 'active', label: 'Attivi' },
            { key: 'used', label: 'Usati' },
            { key: 'expired', label: 'Scaduti' },
          ].map(f => (
            <button key={f.key} onClick={() => { setStatusFilter(f.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === f.key ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'}`}
              data-testid={`coupon-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-slate-300 focus:border-amber-500/50 focus:outline-none"
          data-testid="coupon-source-filter">
          <option value="all">Tutte le fonti</option>
          <option value="review_reward">Recensione</option>
          <option value="welcome">Benvenuto</option>
          <option value="referral_reward">Referral</option>
          <option value="manual">Manuale</option>
        </select>
        {!showCreate && (
          <button onClick={() => { setShowCreate(true); setCreateResult(null); }}
            className="bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all whitespace-nowrap"
            data-testid="show-create-coupon">
            <Ticket className="w-3 h-3" /> Crea Coupon
          </button>
        )}
      </div>

      <p className="text-slate-500 text-xs">{total} coupon trovati</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Nessun coupon trovato</div>
      ) : (
        <div className="space-y-2">
          {coupons.map((c, i) => {
            const expired = isExpired(c.expires_at);
            const used = (c.used_count || 0) >= (c.max_uses || 1);
            return (
              <motion.div key={c.code || i}
                initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`bg-white/5 border rounded-xl p-4 ${expired || used ? 'border-white/5 opacity-60' : 'border-white/10'}`}
                data-testid={`coupon-row-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-9 h-9 bg-amber-500/10 rounded-lg flex items-center justify-center">
                      <Ticket className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-mono font-bold">{c.code}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sourceColors[c.source] || 'bg-slate-500/20 text-slate-400'}`}>
                          {sourceLabels[c.source] || c.source || 'N/A'}
                        </span>
                        {expired && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400">Scaduto</span>}
                        {used && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400">Usato</span>}
                      </div>
                      <p className="text-slate-500 text-xs">
                        {c.amount}% sconto | Usi: {c.used_count || 0}/{c.max_uses || 1}
                        {c.user_email ? ` | ${c.user_email}` : ''}
                        {c.expires_at ? ` | Scade: ${new Date(c.expires_at).toLocaleDateString('it-IT')}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-slate-600 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString('it-IT') : ''}</p>
                    <button onClick={() => deleteCoupon(c.code)}
                      className="p-1.5 text-slate-600 hover:text-rose-400 transition-all" data-testid={`delete-coupon-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-slate-400 text-sm px-3">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 disabled:opacity-30 hover:bg-white/10 transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ──────────── REVENUE CHART ──────────── */
function RevenueChart({ token }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [view, setView] = useState('revenue');

  const fetchChart = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue-chart`, {
        params: { days },
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartData(res.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-slate-400 text-xs mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} className="text-sm font-mono" style={{ color: entry.color }}>
            {entry.name === 'revenue' ? `€${entry.value.toFixed(2)}` : `${entry.value} ordini`}
          </p>
        ))}
      </div>
    );
  };

  const totalRevenue = chartData.reduce((s, d) => s + d.revenue, 0).toFixed(2);
  const totalOrders = chartData.reduce((s, d) => s + d.orders, 0);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="revenue-chart">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-emerald-500/20 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Andamento Ricavi</h3>
            <p className="text-slate-500 text-xs">
              {"\u20AC"}{totalRevenue} totale | {totalOrders} ordini ({days}gg)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
            {[
              { key: 'revenue', label: 'Ricavi' },
              { key: 'orders', label: 'Ordini' },
            ].map(v => (
              <button key={v.key} onClick={() => setView(v.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${view === v.key ? 'bg-amber-500 text-black' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`chart-view-${v.key}`}>
                {v.label}
              </button>
            ))}
          </div>
          {/* Period selector */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
            {[
              { d: 7, label: '7g' },
              { d: 30, label: '30g' },
              { d: 90, label: '90g' },
            ].map(p => (
              <button key={p.d} onClick={() => setDays(p.d)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${days === p.d ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`chart-period-${p.d}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : chartData.length === 0 ? (
        <div className="text-center py-16 text-slate-500">Nessun dato disponibile</div>
      ) : view === 'revenue' ? (
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false}
              interval={days <= 7 ? 0 : days <= 30 ? 4 : 13} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={50}
              tickFormatter={v => `€${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="revenue" name="revenue" stroke="#10b981" strokeWidth={2}
              fill="url(#revenueGrad)" dot={false} activeDot={{ r: 4, fill: '#10b981', stroke: '#0d1117', strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false}
              interval={days <= 7 ? 0 : days <= 30 ? 4 : 13} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={30}
              allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="orders" name="orders" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* ──────────── BACKUP TAB ──────────── */
function BackupTab({ token }) {
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/backups`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/backup-settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBackups(bRes.data.backups || []);
      setSettings(sRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createBackup = async () => {
    setCreating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/backups/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setResult({ success: true, msg: `Backup creato: ${res.data.name} (${res.data.size_mb} MB)` });
        fetchData();
      } else {
        setResult({ success: false, msg: res.data.error || 'Errore' });
      }
    } catch (e) {
      setResult({ success: false, msg: e.response?.data?.detail || 'Errore creazione backup' });
    }
    setCreating(false);
  };

  const uploadToPcloud = async (name) => {
    setUploading(name);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/backups/${name}/upload-pcloud`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setResult({ success: true, msg: `Caricato su pCloud: ${res.data.pcloud_path}` });
        fetchData();
      } else {
        setResult({ success: false, msg: res.data.error || 'Errore upload' });
      }
    } catch (e) {
      setResult({ success: false, msg: e.response?.data?.detail || 'Errore upload pCloud' });
    }
    setUploading(null);
  };

  const deleteBackup = async (name) => {
    if (!window.confirm(`Eliminare il backup ${name}?`)) return;
    try {
      await axios.delete(`${API}/admin/backups/${name}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch { /* ignore */ }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/backup-settings`, {
        enabled: settings.enabled,
        frequency_days: settings.frequency_days,
        auto_pcloud: settings.auto_pcloud,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult({ success: true, msg: 'Impostazioni salvate' });
    } catch {
      setResult({ success: false, msg: 'Errore salvataggio' });
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="backup-tab">
      {/* Manual Backup */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Backup Manuale</h3>
              <p className="text-slate-500 text-xs">Crea un backup completo del database</p>
            </div>
          </div>
          <button onClick={createBackup} disabled={creating}
            className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            data-testid="create-backup-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {creating ? 'Backup in corso...' : 'Crea Backup Ora'}
          </button>
        </div>

        {result && (
          <div className={`p-3 rounded-lg text-sm mb-4 ${result.success ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'}`}>
            {result.msg}
          </div>
        )}

        {/* Backup List */}
        {backups.length === 0 ? (
          <div className="text-center py-8 text-slate-500">Nessun backup disponibile</div>
        ) : (
          <div className="space-y-2">
            {backups.map((b, i) => (
              <div key={b.name || i}
                className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center">
                    <Database className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-mono">{b.filename}</p>
                    <div className="flex items-center gap-3 text-slate-500 text-xs mt-0.5">
                      <span>{b.size_mb} MB</span>
                      <span>{b.created_at ? new Date(b.created_at).toLocaleString('it-IT') : ''}</span>
                      {!b.file_exists && <span className="text-rose-400">File mancante</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {b.pcloud_uploaded ? (
                    <span className="flex items-center gap-1 text-emerald-400 text-xs bg-emerald-500/10 px-2 py-1 rounded-full">
                      <Cloud className="w-3 h-3" /> pCloud
                    </span>
                  ) : b.file_exists && settings?.pcloud_configured ? (
                    <button onClick={() => uploadToPcloud(b.name)} disabled={uploading === b.name}
                      className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs bg-blue-500/10 hover:bg-blue-500/20 px-2.5 py-1 rounded-full transition-all disabled:opacity-50"
                      data-testid={`upload-pcloud-${i}`}>
                      {uploading === b.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloudUpload className="w-3 h-3" />}
                      pCloud
                    </button>
                  ) : null}
                  <button onClick={() => deleteBackup(b.name)}
                    className="p-1.5 text-slate-600 hover:text-rose-400 transition-all" data-testid={`delete-backup-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto Backup Settings */}
      {settings && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Backup Automatico</h3>
              <p className="text-slate-500 text-xs">Programma backup periodici{settings.pcloud_configured ? ' con upload pCloud' : ''}</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Enable toggle */}
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Backup automatico</span>
              <button onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}
                className={`w-12 h-6 rounded-full transition-all relative ${settings.enabled ? 'bg-amber-500' : 'bg-white/10'}`}
                data-testid="auto-backup-toggle">
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${settings.enabled ? 'left-6' : 'left-0.5'}`} />
              </button>
            </div>

            {/* Frequency */}
            {settings.enabled && (
              <>
                <div>
                  <label className="text-slate-400 text-xs uppercase tracking-wider mb-2 block">Frequenza</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { days: 3, label: 'Ogni 3 giorni' },
                      { days: 7, label: 'Ogni 7 giorni' },
                      { days: 15, label: 'Ogni 15 giorni' },
                    ].map(opt => (
                      <button key={opt.days} onClick={() => setSettings(s => ({ ...s, frequency_days: opt.days }))}
                        className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${settings.frequency_days === opt.days ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'}`}
                        data-testid={`freq-${opt.days}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Auto pCloud upload */}
                {settings.pcloud_configured && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-blue-400" />
                      <span className="text-slate-300 text-sm">Upload automatico su pCloud</span>
                    </div>
                    <button onClick={() => setSettings(s => ({ ...s, auto_pcloud: !s.auto_pcloud }))}
                      className={`w-12 h-6 rounded-full transition-all relative ${settings.auto_pcloud ? 'bg-blue-500' : 'bg-white/10'}`}
                      data-testid="auto-pcloud-toggle">
                      <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${settings.auto_pcloud ? 'left-6' : 'left-0.5'}`} />
                    </button>
                  </div>
                )}
              </>
            )}

            {!settings.pcloud_configured && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm text-amber-300">
                Per abilitare pCloud, aggiungi PCLOUD_EMAIL e PCLOUD_PASSWORD nelle variabili d'ambiente del backend.
              </div>
            )}

            <button onClick={saveSettings} disabled={saving}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              data-testid="save-backup-settings">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
              Salva Impostazioni
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────── TRIGGER BUTTON ──────────── */
function TriggerButton({ label, endpoint, token }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const trigger = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}${endpoint}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <button onClick={trigger} disabled={loading}
      className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-slate-300 transition-all disabled:opacity-50">
      <span>{label}</span>
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-teal-400" /> :
       done ? <span className="text-emerald-400 text-xs">Fatto!</span> :
       <ChevronRight className="w-4 h-4 text-slate-600" />}
    </button>
  );
}

/* ──────────── MAIN ADMIN PANEL ──────────── */
/* ──────────── INVOICES TAB ──────────── */
function InvoicesTab({ token }) {
  const [data, setData] = useState({ eu: [], us: [] });
  const [loading, setLoading] = useState(true);
  const [activeEntity, setActiveEntity] = useState('eu');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      // Auto-expand first month
      const months = res.data[activeEntity] || [];
      if (months.length > 0 && !expandedMonth) {
        setExpandedMonth(months[0].month_key);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, activeEntity, expandedMonth]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownloadZip = async (entity, monthKey = '') => {
    const key = `${entity}-${monthKey || 'all'}`;
    setDownloading(key);
    try {
      const params = new URLSearchParams({ entity });
      if (monthKey) params.append('month', monthKey);
      const res = await axios.get(`${API}/admin/invoices/download-zip?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_${entity.toUpperCase()}_${monthKey || 'all'}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Nessuna fattura trovata'); }
    setDownloading(null);
  };

  const handleDownloadPdf = (invoiceId, invoiceNumber) => {
    window.open(`${API}/invoices/${invoiceId}/pdf`, '_blank');
  };

  const months = data[activeEntity] || [];
  const totalInvoices = months.reduce((sum, m) => sum + m.count, 0);
  const totalAmount = months.reduce((sum, m) => sum + m.total, 0);

  return (
    <div className="space-y-6" data-testid="admin-invoices-tab">
      {/* Entity Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
          <button
            onClick={() => { setActiveEntity('eu'); setExpandedMonth(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeEntity === 'eu' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="admin-invoices-eu-tab"
          >
            <Building2 className="w-4 h-4" />
            Fatture EU
          </button>
          <button
            onClick={() => { setActiveEntity('us'); setExpandedMonth(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeEntity === 'us' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="admin-invoices-us-tab"
          >
            <Globe className="w-4 h-4" />
            Fatture US
          </button>
        </div>

        <button
          onClick={() => handleDownloadZip(activeEntity)}
          disabled={downloading === `${activeEntity}-all` || totalInvoices === 0}
          className="ml-auto flex items-center gap-2 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-40"
          data-testid="admin-invoices-download-all"
        >
          {downloading === `${activeEntity}-all` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
          Scarica tutte ({activeEntity.toUpperCase()})
        </button>

        <button onClick={fetchInvoices} className="text-slate-400 hover:text-white transition-colors p-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-2xl font-bold text-white font-mono">{totalInvoices}</p>
          <p className="text-slate-500 text-xs mt-1">Fatture {activeEntity === 'eu' ? 'EU' : 'US'}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-2xl font-bold text-teal-400 font-mono">&euro;{totalAmount.toFixed(2)}</p>
          <p className="text-slate-500 text-xs mt-1">Totale fatturato</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-400 font-mono">{months.length}</p>
          <p className="text-slate-500 text-xs mt-1">Mesi attivi</p>
        </div>
      </div>

      {/* Months List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
        </div>
      ) : months.length === 0 ? (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessuna fattura {activeEntity === 'eu' ? 'EU' : 'US'} emessa</p>
        </div>
      ) : (
        <div className="space-y-3">
          {months.map((month) => (
            <div key={month.month_key} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid={`admin-invoice-month-${month.month_key}`}>
              {/* Month Header */}
              <button
                onClick={() => setExpandedMonth(expandedMonth === month.month_key ? null : month.month_key)}
                className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">{month.label}</p>
                    <p className="text-slate-500 text-xs">{month.count} fattur{month.count === 1 ? 'a' : 'e'} &middot; &euro;{month.total.toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownloadZip(activeEntity, month.month_key); }}
                    disabled={downloading === `${activeEntity}-${month.month_key}`}
                    className="flex items-center gap-1.5 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                    data-testid={`admin-invoice-download-month-${month.month_key}`}
                  >
                    {downloading === `${activeEntity}-${month.month_key}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                    ZIP
                  </button>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedMonth === month.month_key ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Invoices List */}
              {expandedMonth === month.month_key && (
                <div className="border-t border-white/10">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">N. Fattura</th>
                        <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Cliente</th>
                        <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Ordine</th>
                        <th className="text-right text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Importo</th>
                        <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Data</th>
                        <th className="text-right text-xs text-slate-500 uppercase tracking-wider px-5 py-3">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {month.invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-invoice-row-${inv.invoice_number}`}>
                          <td className="px-5 py-3">
                            <span className="text-teal-400 font-mono text-sm font-medium">{inv.invoice_number}</span>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-white text-sm">{inv.billing_data?.company_name || inv.customer_email || '—'}</p>
                            {inv.billing_data?.tax_id && (
                              <p className="text-slate-500 text-xs">{inv.billing_data.tax_id}</p>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-slate-400 text-sm">#{inv.order_number}</span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-white font-mono font-bold text-sm">&euro;{inv.amount?.toFixed(2)}</span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-slate-400 text-sm">{new Date(inv.created_at).toLocaleDateString('it-IT')}</span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                              className="inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
                              data-testid={`admin-download-invoice-${inv.invoice_number}`}
                            >
                              <Download className="w-3 h-3" />
                              PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ──────────── IMPORT ESIM ──────────── */
function ImportEsimTab({ token }) {
  const [form, setForm] = useState({
    customer_email: '',
    customer_name: '',
    iccid: '',
    smdp_address: '',
    activation_code: '',
    product_name: '',
    price: '',
    purchase_date: '',
    country: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/import-esim`, {
        ...form,
        price: parseFloat(form.price) || 0,
      }, { headers });
      setResult({ success: true, message: res.data.message, iccid: res.data.iccid, sim_id: res.data.sim_id });
      setForm({ customer_email: '', customer_name: '', iccid: '', smdp_address: '', activation_code: '', product_name: '', price: '', purchase_date: '', country: '' });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || 'Errore durante l\'importazione' });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="import-esim-tab">
      <div>
        <h2 className="text-white font-semibold text-lg flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-amber-400" /> Importa eSIM
        </h2>
        <p className="text-slate-400 text-sm mt-1">Associa una eSIM esistente all'account di un cliente. L'account verrà creato automaticamente se non esiste.</p>
      </div>

      {result && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${result.success ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20'}`} data-testid="import-result">
          {result.success ? <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5" />}
          <div>
            <p className={`text-sm font-medium ${result.success ? 'text-emerald-300' : 'text-rose-300'}`}>{result.message}</p>
            {result.success && result.iccid && (
              <p className="text-xs text-slate-400 mt-1">ICCID: {result.iccid} {result.sim_id ? `| SIM ID: ${result.sim_id}` : '| SIM ID non trovato (monitoraggio dati non disponibile)'}</p>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5" data-testid="import-esim-form">
        {/* Required fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Email Cliente *</label>
            <input type="email" required value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              placeholder="cliente@email.com" data-testid="import-email" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Nome Cliente</label>
            <input type="text" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              placeholder="Nome Cognome" data-testid="import-name" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">ICCID *</label>
            <input type="text" required value={form.iccid} onChange={e => setForm(f => ({ ...f, iccid: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors font-mono"
              placeholder="8948010015990673" data-testid="import-iccid" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Nome Prodotto</label>
            <input type="text" value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              placeholder="Global Lite 25GB / 30 Days" data-testid="import-product" />
          </div>
        </div>

        {/* QR code fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">SMDP Address</label>
            <input type="text" value={form.smdp_address} onChange={e => setForm(f => ({ ...f, smdp_address: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors font-mono"
              placeholder="rsp.truphone.com" data-testid="import-smdp" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Activation Code</label>
            <input type="text" value={form.activation_code} onChange={e => setForm(f => ({ ...f, activation_code: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors font-mono"
              placeholder="KO-2V5MV8-N0FA5T" data-testid="import-code" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Prezzo (USD)</label>
            <input type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              placeholder="34.45" data-testid="import-price" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Data Acquisto</label>
            <input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              data-testid="import-date" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 font-medium">Paese / Regione</label>
            <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/40 transition-colors"
              placeholder="Global" data-testid="import-country" />
          </div>
        </div>

        <div className="pt-2">
          <button type="submit" disabled={loading}
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-black font-semibold px-6 py-2.5 rounded-xl text-sm transition-all flex items-center gap-2"
            data-testid="import-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
            Importa eSIM
          </button>
        </div>

        <p className="text-xs text-slate-500">* Campi obbligatori. Se il SMDP Address e Activation Code vengono forniti, il QR code verrà generato automaticamente.</p>
      </form>
    </div>
  );
}


export default function AdminPanel({ onBack }) {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchDashboard = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const [dashRes, revRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/revenue-summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(dashRes.data);
      setRevenue(revRes.data);
    } catch {
      localStorage.removeItem('admin_token');
      setToken('');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/refresh-catalog`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchDashboard();
    } catch { /* ignore */ }
    setRefreshing(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setData(null);
  };

  if (!token) return <AdminLogin onLogin={(t) => { setToken(t); setLoading(true); }} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;
  const { stats, pricing, esimcard } = data;

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { key: 'orders', label: 'Ordini', icon: ShoppingBag },
    { key: 'invoices', label: 'Fatture', icon: FileText },
    { key: 'users', label: 'Utenti', icon: Users },
    { key: 'coupons', label: 'Coupon', icon: Ticket },
    { key: 'alerts', label: 'Avvisi Rete', icon: AlertTriangle },
    { key: 'import', label: 'Importa eSIM', icon: Smartphone },
    { key: 'backup', label: 'Backup', icon: HardDrive },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e14]" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="text-slate-400 hover:text-white p-1 transition-all" data-testid="admin-back">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Shield className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-white font-bold">Admin Panel</h1>
              <p className="text-slate-500 text-xs">eSIM4You Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleRefresh} disabled={refreshing}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all"
              data-testid="refresh-catalog">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna Catalogo
            </button>
            <button onClick={handleLogout}
              className="text-slate-500 hover:text-white text-sm px-3 py-2 transition-all" data-testid="admin-logout">
              Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-white/10 pb-0 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.key ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
              data-testid={`admin-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {activeTab === 'dashboard' && (<>
          {/* Revenue Summary */}
          {revenue && (
            <div className="grid grid-cols-3 gap-3 mb-2">
              {[
                { label: 'Oggi', data: revenue.today, color: 'border-emerald-500/30' },
                { label: 'Ultimi 7 giorni', data: revenue.week, color: 'border-blue-500/30' },
                { label: 'Ultimi 30 giorni', data: revenue.month, color: 'border-amber-500/30' },
              ].map((p, i) => (
                <div key={i} className={`bg-white/5 border ${p.color} rounded-xl p-4`}>
                  <p className="text-slate-500 text-xs mb-1">{p.label}</p>
                  <p className="text-white font-mono font-bold text-xl">{"\u20AC"}{p.data?.revenue || 0}</p>
                  <p className="text-slate-600 text-xs">{p.data?.orders || 0} ordini</p>
                </div>
              ))}
            </div>
          )}

          {/* Revenue Chart */}
          <RevenueChart token={token} />

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-stats">
            <StatCard icon={Package} label="Prodotti" value={stats.products} color="text-blue-400" />
            <StatCard icon={Users} label="Utenti" value={stats.users} color="text-teal-400" />
            <StatCard icon={CreditCard} label="Ordini" value={stats.orders} color="text-emerald-400" />
            <StatCard icon={TrendingUp} label="Fatturato Totale" value={`\u20AC${stats.revenue}`} color="text-amber-400" />
            <StatCard icon={Star} label="Recensioni" value={stats.reviews} color="text-yellow-400" />
            <StatCard icon={Gift} label="Referral" value={stats.referrals} color="text-purple-400" />
            <StatCard icon={Ticket} label="Coupon" value={stats.coupons} color="text-cyan-400" />
            <StatCard icon={Wallet} label="Saldo eSIMCard" value={`$${esimcard.balance}`} color="text-rose-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MarkupControl currentMarkup={pricing.markup} token={token} onUpdate={fetchDashboard} />
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Sistema</h3>
                  <p className="text-slate-500 text-xs">Stato e configurazione</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Catalogo', value: `eSIMCard API (${stats.products} paesi)`, ok: stats.products > 0 },
                  { label: 'Variazioni totali', value: stats.variations, ok: true },
                  { label: 'Markup attuale', value: `${pricing.markup}x`, ok: true },
                  { label: 'Conversione USD/EUR', value: pricing.usd_to_eur, ok: true },
                  { label: 'Saldo fornitore', value: `$${esimcard.balance}`, ok: esimcard.balance > 50 },
                  { label: 'Aggiornamento', value: 'Ogni 2 ore', ok: true },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-slate-400 text-sm">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-mono">{item.value}</span>
                      <div className={`w-2 h-2 rounded-full ${item.ok ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Azioni Manuali</p>
                {[
                  { label: 'Invia richieste recensione', endpoint: '/admin/trigger-review-check' },
                  { label: 'Controlla eSIM in scadenza', endpoint: '/admin/trigger-esim-expiry-check' },
                  { label: 'Controlla wishlist in offerta', endpoint: '/admin/trigger-wishlist-check' },
                ].map(action => (
                  <TriggerButton key={action.endpoint} label={action.label} endpoint={action.endpoint} token={token} />
                ))}
              </div>
            </div>
          </div>

          {/* Zone & Country Markup */}
          <ZoneMarkupControl pricing={pricing} token={token} onUpdate={fetchDashboard} />
        </>)}

        {activeTab === 'orders' && <OrdersTab token={token} />}
        {activeTab === 'invoices' && <InvoicesTab token={token} />}
        {activeTab === 'users' && <UsersTab token={token} />}
        {activeTab === 'coupons' && <CouponsTab token={token} />}
        {activeTab === 'alerts' && <NetworkAlertsTab token={token} />}
        {activeTab === 'import' && <ImportEsimTab token={token} />}
        {activeTab === 'backup' && <BackupTab token={token} />}
      </div>
    </div>
  );
}
